import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel
} from "@tanstack/react-table";
import { Business, type BusinessSubscription } from "lens-platform-sdk";
import { Popover } from "@headlessui/react";

import { dateToString } from "src/utils/date";
import { trimSubscriptionId } from "src/services/trimSubscriptionId";
import { getPlanName } from "src/services/plans";

import Table from "src/components/Table/Table";

import styles from "src/components/Business/SubscriptionTable.module.css";
import { useCallback, useMemo } from "react";
import { misc } from "@k8slens/lds-icons";
import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { useHistory, useParams } from "react-router-dom";
import { ContextMenuIcon, InfoIcon, EditIcon } from "src/components/Icon/Navigation";
import { useTrackedTableSort } from "./useTrackedTableSort";

const columnHelper = createColumnHelper<BusinessSubscription>();

interface Props {
  loading?: boolean;
  businessSubscriptions: BusinessSubscription[];
  business: Business | undefined;
}

const caption = "Subscriptions";

const { LockIcon } = misc;

/**
 * The business subscriptions table used in business root page (/business/:businessId)
 */
const SubscriptionTable = ({ loading, businessSubscriptions, business }: Props) => {
  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });
  const history = useHistory();
  const { businessId } = useParams<{ businessId?: string }>();
  const handleOpenSubscriptionModal = useCallback((subscription: BusinessSubscription) => {
    history.push(`/business/${businessId}/subscriptions/${subscription.id}`);
  }, [businessId, history]);

  const columns = useMemo(() => [
    columnHelper.accessor("id", {
      header: () => <span>Subscription ID</span>,
      cell: ({ getValue }) => (
        <span
          className={styles.subscriptionId}
          title={getValue()}
        >
          {trimSubscriptionId(getValue())}
        </span>
      ),
      meta: { primary: true }
    }),
    columnHelper.accessor("planCode", {
      header: () => <span>Plan</span>,
      cell: ({ getValue }) => <span>{getPlanName(getValue())}</span>
    }),
    columnHelper.accessor("currentPeriodEndsAt", {
      header: () => <span>Next Renewal Date</span>,
      cell: ({ getValue }) => <span>{dateToString(new Date(getValue() ?? ""))}</span>
    }),
    columnHelper.accessor("seats", {
      header: () => <span>Seats</span>,
      cell: ({ getValue, row }) => {
        const subscription = row.original;

        return (
          <div className={styles.quantity}>
            <span>{getValue()}</span>
            {subscription?.pendingChange?.quantity && subscription?.pendingChange?.activateAt && (
            <span className={styles.pendingChange}>
              <Popover>
                <Popover.Button className={styles.pendingChangeButton}><InfoIcon size="sm" color="caution" /></Popover.Button>
                <Popover.Panel className={styles.pendingChangePopoverContent}>
                  {subscription?.pendingChange?.quantity}
                  {" "}
                  from
                  {" "}
                  {dateToString(new Date(subscription?.pendingChange?.activateAt))}
                </Popover.Panel>
              </Popover>
            </span>
            )}
          </div>
        );
      }
    }),
    columnHelper.accessor("state", {
      header: () => <span>Status</span>,
      cell: ({ getValue }) => <span className={styles[getValue()] ?? styles.defaultState}>{getValue()}</span>
    }),
    columnHelper.accessor("id", {
      header: () => <span />,
      enableSorting: false,
      cell: ({ row }) => {
        const subscription = row.original;
        const isSubscriptionUpgradable = !(business?.external || subscription.state === "expired");

        return (
          <div className={styles.menuContainer}>
            <ContextMenu toggleIcon={ContextMenuIcon} toggleButtonProps={{ title: "Toggle context menu" }}>
              <ContextMenuItem disabled={!isSubscriptionUpgradable} label="Change seats" icon={isSubscriptionUpgradable ? EditIcon : LockIcon} onClick={() => handleOpenSubscriptionModal(row.original)} />
            </ContextMenu>
          </div>
        );
      }
    })
  ], [handleOpenSubscriptionModal, business?.external]);

  const table = useReactTable({
    data: businessSubscriptions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const tableRowModel = table.getRowModel();
  const tableRows = useMemo(() => tableRowModel.rows, [tableRowModel]);

  return (
    <>
      <Table<BusinessSubscription>
        caption="Subscriptions"
        rows={tableRows}
        columns={table.getAllColumns()}
        headerGroups={table.getHeaderGroups()}
        loading={loading}
        headerGroupProps={{
          cellClassName: styles.tableHeader
        }}
        rowProps={{
          className: styles.tableRow,
          cellClassName: styles.tableCell
        }}
        noDataText="No subscriptions for your Lens Business ID!"
      />
    </>
  );
};

export default SubscriptionTable;
